import { Box, Paper, Stack, Typography } from "@mui/material";
import { CopyableText } from "../../components/CopyableText";
import type { IndexErrorRowDto } from "../../api/types";

export type IndexErrorRowsProps = {
  rows: IndexErrorRowDto[];
  total: number;
};

/**
 * Error placeholder rows from `transaction_index`: transactions the last
 * rebuild could not load or project. They are hidden from every other view,
 * so this is the only place a failed transaction surfaces.
 */
export const IndexErrorRows = ({ rows, total }: IndexErrorRowsProps) => (
  <Paper sx={{ p: 3, mb: 3 }} data-testid="index-error-rows">
    <Typography variant="h6" gutterBottom>
      Index Errors
    </Typography>
    <Typography variant="body2" gutterBottom sx={{ color: "text.secondary" }}>
      {total === 1
        ? "1 transaction could not be indexed and is hidden from all views."
        : `${total} transactions could not be indexed and are hidden from all views.`}{" "}
      Fix the stored transaction, then rebuild the index.
    </Typography>
    {rows.length < total && (
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Showing the newest {rows.length} of {total}.
      </Typography>
    )}
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {rows.map((row) => (
        <Box
          key={row.storedTxId}
          sx={{ borderLeft: 3, borderColor: "error.main", pl: 1.5 }}
        >
          <CopyableText
            text={row.storedTxId}
            typographyProps={{ sx: { fontFamily: "monospace" } }}
          />
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {row.error}
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            sx={{ flexWrap: "wrap", alignItems: "center" }}
          >
            {row.importId && (
              <CopyableText
                label="Import"
                text={row.importId}
                typographyProps={{ variant: "caption" }}
              />
            )}
            {(row.datePaid || row.merchant) && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {[row.datePaid, row.merchant].filter(Boolean).join(" · ")}
              </Typography>
            )}
          </Stack>
        </Box>
      ))}
    </Stack>
  </Paper>
);
